import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OrgWidgetData } from '../../models/org-widget.models';
import { OrgDialogService } from '../../../org-board/services/org-dialog.service';
import { OrgEntityColorsService } from '../../services/org-entity-colors.service';

interface UncoveredUser {
    uid: string;
    name: string;
    email: string;
    data: Record<string, unknown>;
}

@Component({
    selector: 'shared-security-coverage-widget',
    imports: [CommonModule, MatIconModule, MatTooltipModule],
    template: `
        <div class="coverage-container" *ngIf="data">
            <!-- Summary bar -->
            <div class="coverage-bar">
                <div
                    class="bar-segment covered"
                    [style.width.%]="coveredPercentage"
                    matTooltip="Staff with security groups: {{ coveredCount }}"
                ></div>
                <div
                    class="bar-segment uncovered"
                    [style.width.%]="100 - coveredPercentage"
                    matTooltip="Staff without security groups: {{ uncoveredCount }}"
                ></div>
            </div>

            <div class="coverage-summary">
                <span class="coverage-percent">{{ coveredPercentage | number: '1.0-0' }}%</span>
                <span class="coverage-label">of staff have security group assignments</span>
            </div>

            <!-- Breakdown -->
            <div class="coverage-details">
                <div
                    class="detail-row clickable"
                    (click)="showCoveredUsers = !showCoveredUsers; showUncoveredUsers = false"
                >
                    <div class="detail-indicator covered-dot"></div>
                    <span class="detail-label">With security groups</span>
                    <span class="detail-value">{{ coveredCount }}</span>
                </div>
                <div
                    class="detail-row clickable"
                    (click)="showUncoveredUsers = !showUncoveredUsers; showCoveredUsers = false"
                >
                    <div class="detail-indicator uncovered-dot"></div>
                    <span class="detail-label">Without security groups</span>
                    <span class="detail-value">{{ uncoveredCount }}</span>
                    <mat-icon class="expand-arrow" [class.expanded]="showUncoveredUsers">expand_more</mat-icon>
                </div>

                <!-- Uncovered users expandable list -->
                <div class="uncovered-users-list" *ngIf="showUncoveredUsers && uncoveredUsers.length > 0">
                    <div class="user-item" *ngFor="let user of uncoveredUsers" (click)="onUserClick(user)">
                        <mat-icon class="user-icon">person_outline</mat-icon>
                        <div class="user-info">
                            <span class="user-name">{{ user.name }}</span>
                            <span class="user-email" *ngIf="user.email">{{ user.email }}</span>
                        </div>
                        <mat-icon class="open-icon" matTooltip="View staff member details">open_in_new</mat-icon>
                    </div>
                </div>
                <div class="uncovered-empty" *ngIf="showUncoveredUsers && uncoveredUsers.length === 0">
                    <mat-icon>check_circle</mat-icon>
                    <span>All staff have security group assignments</span>
                </div>

                <!-- Covered users expandable list -->
                <div class="uncovered-users-list" *ngIf="showCoveredUsers && coveredUsers.length > 0">
                    <div class="user-item" *ngFor="let user of coveredUsers" (click)="onUserClick(user)">
                        <mat-icon class="user-icon" style="color: #43a047">person</mat-icon>
                        <div class="user-info">
                            <span class="user-name">{{ user.name }}</span>
                            <span class="user-email" *ngIf="user.email">{{ user.email }}</span>
                        </div>
                        <mat-icon class="open-icon" matTooltip="View staff member details">open_in_new</mat-icon>
                    </div>
                </div>

                <div class="detail-row total">
                    <span class="detail-label">Total staff</span>
                    <span class="detail-value">{{ totalUsers }}</span>
                </div>
            </div>

            <!-- Security groups list -->
            <div class="sg-list" *ngIf="groupBreakdown.length > 0">
                <div class="sg-header">Security Groups</div>
                <div class="sg-item" *ngFor="let group of groupBreakdown" (click)="onSgClick(group)">
                    <mat-icon class="sg-icon" [style.color]="colorsService.getColor('securityGroups')">shield</mat-icon>
                    <span class="sg-name">{{ group.name }}</span>
                    <span class="sg-count"
                        >{{ group.memberCount }} {{ group.memberCount === 1 ? 'member' : 'members' }}</span
                    >
                </div>
            </div>
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }

            .coverage-container {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            /* Progress bar */
            .coverage-bar {
                display: flex;
                height: 10px;
                border-radius: 5px;
                overflow: hidden;
                background: rgba(0, 0, 0, 0.06);
            }

            .bar-segment {
                transition: width 0.4s ease;

                &.covered {
                    background: #43a047;
                }
                &.uncovered {
                    background: #ef5350;
                }
            }

            /* Summary */
            .coverage-summary {
                display: flex;
                align-items: baseline;
                gap: 8px;
            }

            .coverage-percent {
                font-size: 28px;
                font-weight: 600;
                color: #43a047;
                line-height: 1;
            }

            .coverage-label {
                font-size: 13px;
                color: rgba(0, 0, 0, 0.54);
            }

            /* Details */
            .coverage-details {
                display: flex;
                flex-direction: column;
                gap: 6px;
            }

            .detail-row {
                display: flex;
                align-items: center;
                gap: 8px;

                &.total {
                    border-top: 1px solid rgba(0, 0, 0, 0.08);
                    padding-top: 6px;
                    margin-top: 2px;
                }
            }

            .detail-indicator {
                width: 10px;
                height: 10px;
                border-radius: 50%;
                flex-shrink: 0;

                &.covered-dot {
                    background: #43a047;
                }
                &.uncovered-dot {
                    background: #ef5350;
                }
            }

            .detail-label {
                flex: 1;
                font-size: 13px;
                color: rgba(0, 0, 0, 0.7);
            }

            .detail-value {
                font-size: 13px;
                font-weight: 500;
                color: rgba(0, 0, 0, 0.87);
            }

            /* Security groups list */
            .sg-list {
                margin-top: 4px;
                overflow-y: auto;
                scrollbar-width: thin;
                scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

                &::-webkit-scrollbar {
                    width: 6px;
                }
                &::-webkit-scrollbar-track {
                    background: transparent;
                    border-radius: 3px;
                }
                &::-webkit-scrollbar-thumb {
                    background: rgba(0, 0, 0, 0.2);
                    border-radius: 3px;
                    &:hover {
                        background: rgba(0, 0, 0, 0.3);
                    }
                }
            }

            .sg-header {
                font-size: 12px;
                font-weight: 500;
                color: rgba(0, 0, 0, 0.54);
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin-bottom: 6px;
            }

            .sg-item {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 4px 0;
                cursor: pointer;
                border-radius: 4px;
                padding: 4px 6px;

                &:hover {
                    background: rgba(0, 0, 0, 0.04);
                }
            }

            .sg-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
                color: var(--entity-color-securityGroups, #f57c00);
            }

            .sg-name {
                flex: 1;
                font-size: 13px;
                color: rgba(0, 0, 0, 0.87);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .sg-count {
                font-size: 12px;
                color: rgba(0, 0, 0, 0.38);
                white-space: nowrap;
            }

            /* Clickable detail rows */
            .detail-row.clickable {
                cursor: pointer;
                border-radius: 4px;
                padding: 2px 6px;
                margin: 0 -6px;
                transition: background 0.15s;

                &:hover {
                    background: rgba(0, 0, 0, 0.04);
                }
            }

            .expand-arrow {
                font-size: 18px;
                width: 18px;
                height: 18px;
                color: rgba(0, 0, 0, 0.38);
                transition: transform 0.2s ease;
                margin-left: 4px;

                &.expanded {
                    transform: rotate(180deg);
                }
            }

            /* Uncovered/Covered users expandable list */
            .uncovered-users-list {
                margin: 4px 0 8px 0;
                padding-left: 18px;
                max-height: 200px;
                overflow-y: auto;
                scrollbar-width: thin;
                scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

                &::-webkit-scrollbar {
                    width: 6px;
                }
                &::-webkit-scrollbar-track {
                    background: transparent;
                    border-radius: 3px;
                }
                &::-webkit-scrollbar-thumb {
                    background: rgba(0, 0, 0, 0.2);
                    border-radius: 3px;
                    &:hover {
                        background: rgba(0, 0, 0, 0.3);
                    }
                }
            }

            .user-item {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 5px 8px;
                border-radius: 4px;
                cursor: pointer;
                transition: background 0.15s;

                &:hover {
                    background: rgba(0, 0, 0, 0.04);

                    .open-icon {
                        opacity: 1;
                    }
                }
            }

            .user-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
                color: #ef5350;
                flex-shrink: 0;
            }

            .user-info {
                flex: 1;
                display: flex;
                flex-direction: column;
                min-width: 0;
            }

            .user-name {
                font-size: 13px;
                color: rgba(0, 0, 0, 0.87);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .user-email {
                font-size: 11px;
                color: rgba(0, 0, 0, 0.38);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .open-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
                color: rgba(0, 0, 0, 0.3);
                opacity: 0;
                transition: opacity 0.15s;
                flex-shrink: 0;
            }

            .uncovered-empty {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 8px 0 8px 18px;
                font-size: 12px;
                color: #43a047;

                mat-icon {
                    font-size: 16px;
                    width: 16px;
                    height: 16px;
                }
            }

            /* Dark theme */
            :host-context(.dark-theme) {
                .coverage-bar {
                    background: rgba(255, 255, 255, 0.08);
                }
                .coverage-label {
                    color: rgba(255, 255, 255, 0.54);
                }
                .detail-label {
                    color: rgba(255, 255, 255, 0.7);
                }
                .detail-value {
                    color: rgba(255, 255, 255, 0.87);
                }
                .detail-row.total {
                    border-top-color: rgba(255, 255, 255, 0.1);
                }
                .detail-row.clickable:hover {
                    background: rgba(255, 255, 255, 0.06);
                }
                .expand-arrow {
                    color: rgba(255, 255, 255, 0.38);
                }
                .sg-header {
                    color: rgba(255, 255, 255, 0.54);
                }
                .sg-name {
                    color: rgba(255, 255, 255, 0.87);
                }
                .sg-count {
                    color: rgba(255, 255, 255, 0.38);
                }
                .sg-item:hover {
                    background: rgba(255, 255, 255, 0.06);
                }
                .sg-list {
                    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
                    &::-webkit-scrollbar-thumb {
                        background: rgba(255, 255, 255, 0.2);
                        &:hover {
                            background: rgba(255, 255, 255, 0.3);
                        }
                    }
                }
                .user-item:hover {
                    background: rgba(255, 255, 255, 0.06);
                }
                .user-name {
                    color: rgba(255, 255, 255, 0.87);
                }
                .user-email {
                    color: rgba(255, 255, 255, 0.38);
                }
                .open-icon {
                    color: rgba(255, 255, 255, 0.3);
                }
                .uncovered-users-list {
                    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
                    &::-webkit-scrollbar-thumb {
                        background: rgba(255, 255, 255, 0.2);
                        &:hover {
                            background: rgba(255, 255, 255, 0.3);
                        }
                    }
                }
            }
        `,
    ],
})
export class SecurityCoverageWidgetComponent implements OnChanges {
    @Input() data!: OrgWidgetData;

    public totalUsers = 0;
    public coveredCount = 0;
    public uncoveredCount = 0;
    public coveredPercentage = 0;
    public groupBreakdown: { key: string; name: string; memberCount: number }[] = [];
    public uncoveredUsers: UncoveredUser[] = [];
    public coveredUsers: UncoveredUser[] = [];
    public showUncoveredUsers = false;
    public showCoveredUsers = false;

    constructor(
        private dialogService: OrgDialogService,
        public colorsService: OrgEntityColorsService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data'] && this.data) {
            this.computeCoverage();
        }
    }

    private computeCoverage(): void {
        const users = this.data.users || [];
        const hats = this.data.hats || [];
        const sgs = this.data.securityGroups || [];
        this.totalUsers = users.length;

        // ── Build sgKey → Set<userUid> from HATS (authoritative source) ──
        // Each hat has securityGroupsArray (SG keys it belongs to) and
        // assignedToArray (user UIDs assigned to that hat).
        // This matches how the org designer computes "Users (via Hats)".
        const sgMembers = new Map<string, Set<string>>();
        for (const sg of sgs) {
            sgMembers.set(sg.key, new Set<string>());
        }

        for (const hat of hats) {
            const d = hat.data as Record<string, unknown>;
            if (!d) continue;

            // Get SG keys from hat — field is securityGroupsArray (may contain strings or {key} objects)
            const sgArr = (d['securityGroupsArray'] ||
                d['securityGroups'] ||
                d['securityGroupsKeys'] ||
                []) as unknown[];
            const hatSgKeys: string[] = [];
            if (Array.isArray(sgArr)) {
                for (const entry of sgArr) {
                    const key =
                        typeof entry === 'string' ? entry : ((entry as Record<string, unknown>)?.['key'] as string);
                    if (key && sgMembers.has(key)) hatSgKeys.push(key);
                }
            }

            // Get users assigned to this hat
            const assignedTo = (d['assignedToArray'] || d['assignedTo'] || []) as string[];
            if (!Array.isArray(assignedTo)) continue;

            // Map each assigned user into each of this hat's SGs
            for (const uid of assignedTo) {
                for (const sgKey of hatSgKeys) {
                    sgMembers.get(sgKey)!.add(uid);
                }
            }
        }

        // ── Coverage: users who appear in at least one SG ──
        const allCoveredUids = new Set<string>();
        for (const members of sgMembers.values()) {
            for (const uid of members) allCoveredUids.add(uid);
        }
        this.coveredCount = allCoveredUids.size;
        // Clamp to totalUsers in case hats reference UIDs not in userKeys (edge case)
        if (this.coveredCount > this.totalUsers) this.coveredCount = this.totalUsers;
        this.uncoveredCount = this.totalUsers - this.coveredCount;
        this.coveredPercentage = this.totalUsers > 0 ? (this.coveredCount / this.totalUsers) * 100 : 0;

        // ── Build uncovered & covered user lists ──
        this.uncoveredUsers = [];
        this.coveredUsers = [];
        for (const user of users) {
            const d = user.data as Record<string, unknown>;
            const name = (d?.['displayName'] || d?.['userName'] || d?.['name'] || d?.['email'] || user.key) as string;
            const email = (d?.['email'] || '') as string;
            const entry: UncoveredUser = { uid: user.key, name, email, data: d };
            if (allCoveredUids.has(user.key)) {
                this.coveredUsers.push(entry);
            } else {
                this.uncoveredUsers.push(entry);
            }
        }
        this.uncoveredUsers.sort((a, b) => a.name.localeCompare(b.name));
        this.coveredUsers.sort((a, b) => a.name.localeCompare(b.name));

        // ── Group breakdown ──
        this.groupBreakdown = sgs
            .map((sg) => {
                const d = sg.data as Record<string, unknown>;
                return {
                    key: sg.key,
                    name: (d?.['name'] || sg.key) as string,
                    memberCount: sgMembers.get(sg.key)?.size || 0,
                };
            })
            .sort((a, b) => b.memberCount - a.memberCount);
    }

    public onSgClick(group: { key: string; name: string }): void {
        const sgEntity = (this.data.securityGroups || []).find((sg) => sg.key === group.key);
        if (sgEntity) {
            this.dialogService.openSecurityGroupDetailsDialog(group.key, sgEntity.data);
        }
    }

    public onUserClick(user: UncoveredUser): void {
        const userForDialog = { key: user.uid, name: user.name, ...user.data };
        this.dialogService.openOrgUserDetailsDialog(null as any, userForDialog);
    }
}
